import React, { useState } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Button, 
  Box,
  TextField,
  Alert,
  Fade,
  Grow,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Chip
} from '@mui/material';
import { 
  GroupAdd,
  ContentCopy,
  CheckCircle,
  SportsEsports,
  Person,
  Timer
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { createDuoGame, joinDuoGame } from '../../services/gameService';

const MotionPaper = motion(Paper);
const MotionCard = motion(Card);

function DuoMode() {
  const [gameCode, setGameCode] = useState('');
  const [isHost, setIsHost] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [players, setPlayers] = useState([]);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const handleCreateGame = async () => {
    setLoading(true);
    try {
      const newGameCode = Math.random().toString(36).substring(2, 8).toUpperCase();
      await createDuoGame(newGameCode, currentUser.uid);
      setGameCode(newGameCode);
      setIsHost(true);
      setPlayers([{ id: currentUser.uid, username: currentUser.username, ready: false }]);
    } catch (error) {
      setError('Oyun oluşturulamadı. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinGame = async () => {
    if (gameCode.length < 6) {
      setError('Lütfen geçerli bir oyun kodu girin');
      return;
    }

    setLoading(true);
    try {
      await joinDuoGame(gameCode.toUpperCase(), currentUser.uid);
      setGameStarted(true);
    } catch (error) {
      setError(error.message || 'Oyuna katılınamadı');
    } finally {
      setLoading(false);
    }
  };

  const copyGameCode = () => {
    navigator.clipboard.writeText(gameCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (gameStarted) {
    return (
      <Fade in timeout={800}>
        <Container maxWidth="sm">
          <MotionPaper
            elevation={3}
            sx={{ p: 4, mt: 4 }}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <SportsEsports sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                Oyun Başladı
              </Typography>
              <Typography color="text.secondary">
                Arkadaşınla yarışmaya hazır ol!
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Timer sx={{ fontSize: 40, color: 'primary.main', mr: 1 }} />
              <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>
                3:00
              </Typography>
            </Box>

            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/dashboard')}
              sx={{ 
                mt: 2,
                height: 48,
                borderColor: '#00bf0d',
                color: '#00bf0d',
                '&:hover': {
                  borderColor: '#00bf0d',
                  backgroundColor: 'rgba(0, 191, 13, 0.1)',
                }
              }}
            >
              Oyundan Çık
            </Button>
          </MotionPaper>
        </Container>
      </Fade>
    );
  }

  return (
    <Fade in timeout={800}>
      <Container maxWidth="sm">
        <MotionPaper
          elevation={3}
          sx={{ p: 4, mt: 4 }}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <GroupAdd sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              İkili Mod
            </Typography>
            <Typography color="text.secondary">
              Arkadaşınla birlikte öğren!
            </Typography>
          </Box>
          
          {error && (
            <Grow in>
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            </Grow>
          )}

          <AnimatePresence mode="wait">
            {isHost ? (
              <MotionCard
                key="host"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.5 }}
                sx={{ mb: 3, overflow: 'hidden' }}
              >
                <CardContent>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Oyun Kodu
                    </Typography>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1
                      }}
                    >
                      <Chip
                        label={gameCode}
                        sx={{ 
                          fontSize: '1.5rem',
                          height: 'auto',
                          padding: '16px',
                          bgcolor: 'rgba(0, 191, 13, 0.1)',
                          color: 'primary.main',
                          fontWeight: 600
                        }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={copyGameCode}
                        startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                        sx={{ 
                          borderColor: '#00bf0d',
                          color: '#00bf0d',
                          '&:hover': {
                            borderColor: '#00bf0d',
                            backgroundColor: 'rgba(0, 191, 13, 0.1)',
                          }
                        }}
                      >
                        {copied ? 'Kopyalandı!' : 'Kopyala'}
                      </Button>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Oyuncular ({players.length}/2)
                    </Typography>
                    {players.map((player, index) => (
                      <Box
                        key={player.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1,
                          p: 1,
                          bgcolor: 'rgba(0, 191, 13, 0.05)',
                          borderRadius: 1
                        }}
                      >
                        <Person sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography>{player.username}</Typography>
                      </Box>
                    ))}
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => setGameStarted(true)}
                    disabled={players.length < 2}
                    sx={{
                      height: 48,
                      background: 'linear-gradient(45deg, #00bf0d 30%, #4cf13d 90%)',
                      boxShadow: '0 3px 5px 2px rgba(0, 191, 13, .3)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #00bf0d 60%, #4cf13d 90%)',
                        boxShadow: '0 4px 10px 2px rgba(0, 191, 13, .4)',
                      }
                    }}
                  >
                    Oyunu Başlat
                  </Button>
                </CardContent>
              </MotionCard>
            ) : (
              <MotionCard
                key="join"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <CardContent>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleCreateGame}
                    disabled={loading}
                    sx={{
                      height: 48,
                      mb: 3,
                      background: 'linear-gradient(45deg, #00bf0d 30%, #4cf13d 90%)',
                      boxShadow: '0 3px 5px 2px rgba(0, 191, 13, .3)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #00bf0d 60%, #4cf13d 90%)',
                        boxShadow: '0 4px 10px 2px rgba(0, 191, 13, .4)',
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Yeni Oyun Oluştur'}
                  </Button>

                  <Divider sx={{ my: 3 }}>
                    <Typography color="text.secondary">VEYA</Typography>
                  </Divider>

                  <TextField
                    fullWidth
                    label="Oyun Kodunu Girin"
                    value={gameCode}
                    onChange={(e) => setGameCode(e.target.value.toUpperCase())}
                    sx={{ mb: 2 }}
                    InputProps={{
                      sx: {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                        },
                      }
                    }}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleJoinGame}
                    disabled={loading}
                    sx={{
                      height: 48,
                      background: 'linear-gradient(45deg, #00bf0d 30%, #4cf13d 90%)',
                      boxShadow: '0 3px 5px 2px rgba(0, 191, 13, .3)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #00bf0d 60%, #4cf13d 90%)',
                        boxShadow: '0 4px 10px 2px rgba(0, 191, 13, .4)',
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Oyuna Katıl'}
                  </Button>
                </CardContent>
              </MotionCard>
            )}
          </AnimatePresence>
        </MotionPaper>
      </Container>
    </Fade>
  );
}

export default DuoMode;
